export interface FeedPrepMeta {
  sourceWidth: number;
  sourceHeight: number;
  feedWidth: number;
  feedHeight: number;
  scale: number;
  padX: number;
  padY: number;
  canvasSize: number;
}

const CANVAS_SIZE = 320;
const STATUS_BAR_RATIO = 0.86;
const YOLO_PAD = 114;

export type PrivacyCanvas = HTMLCanvasElement | OffscreenCanvas;

export function prepareFeedCanvas(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  targetCanvas: PrivacyCanvas
): FeedPrepMeta {
  const feedHeight = Math.max(1, Math.floor(sourceHeight * STATUS_BAR_RATIO));
  const feedWidth = sourceWidth;

  const scale = Math.min(CANVAS_SIZE / feedWidth, CANVAS_SIZE / feedHeight);
  const scaledWidth = Math.max(1, Math.round(feedWidth * scale));
  const scaledHeight = Math.max(1, Math.round(feedHeight * scale));
  const padX = Math.floor((CANVAS_SIZE - scaledWidth) / 2);
  const padY = Math.floor((CANVAS_SIZE - scaledHeight) / 2);

  targetCanvas.width = CANVAS_SIZE;
  targetCanvas.height = CANVAS_SIZE;
  const ctx = targetCanvas.getContext("2d", {
    willReadFrequently: true,
  }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) {
    throw new Error("Could not create feed canvas");
  }

  ctx.fillStyle = `rgb(${YOLO_PAD}, ${YOLO_PAD}, ${YOLO_PAD})`;
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  ctx.drawImage(
    source,
    0,
    0,
    feedWidth,
    feedHeight,
    padX,
    padY,
    scaledWidth,
    scaledHeight
  );

  return {
    sourceWidth,
    sourceHeight,
    feedWidth,
    feedHeight,
    scale,
    padX,
    padY,
    canvasSize: CANVAS_SIZE,
  };
}

export function mapBoxToSource(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  meta: FeedPrepMeta
): { x: number; y: number; width: number; height: number } {
  const left = (x1 - meta.padX) / meta.scale;
  const top = (y1 - meta.padY) / meta.scale;
  const width = (x2 - x1) / meta.scale;
  const height = (y2 - y1) / meta.scale;

  return {
    x: clamp(left, 0, meta.feedWidth),
    y: clamp(top, 0, meta.feedHeight),
    width: clamp(width, 0, meta.feedWidth - left),
    height: clamp(height, 0, meta.feedHeight - top),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
